import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { BlogContext, contentfulClient } from '../App';
import { BlogPosts } from '../components/Resources/Data';

// ===============
// Styled Components
// ===============

// Page wrapper to ensure white background edge‑to‑edge
const PageWrap = styled.div`
  background: #ffffff;
  min-height: 100vh;
`;

// Scroll Progress Bar
const ProgressBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 4px;
  background-color: #e11d48; /* brand accent */
  z-index: 9999;
  transition: width 0.2s ease;
`;

// Constrains the article width and centers it, and scopes brand font vars locally
const Article = styled.article`
  /* Scoped brand typography variables (only within this article) */
  --font-display: 'Alumni Sans', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
  --font-body: 'Bitter', ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif;

  max-width: 860px;
  padding: 2rem 1rem 3rem;
  margin: 0 auto;
  font-family: var(--font-body);
`;

// Title
const Title = styled.h1`
  font-family: var(--font-display);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: clamp(1.75rem, 3.25vw, 2.5rem);
  line-height: 1.1;
  margin: 0.25rem 0 0.75rem;
  color: #101828;
`;

// Meta bar (author avatar, name, date)
const MetaBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  color: #475467;
  font-family: var(--font-body);
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1.25rem;
`;

const ShareGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.5rem;
`;

const ShareLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  text-decoration: none;
  color: #fff;
  background: #344054;
  transition: transform 0.15s ease, background 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    background: #101828;
  }
  svg {
    width: 16px;
    height: 16px;
    display: block;
  }
`;

const ShareButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  color: #fff;
  background: #344054;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    background: #101828;
  }
  svg {
    width: 16px;
    height: 16px;
    display: block;
  }
`;

const Dot = styled.span`
  margin: 0 0.25rem;
  color: #98a2b3;
`;

const Avatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`;

// Hero image container
const Hero = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  background: transparent;
  overflow: hidden; /* clip image to rounded corners */
  border-radius: 16px; /* rounded corners applied by wrapper */
  box-shadow: none;
  /* extra safety for some browsers/framework overrides */
  -webkit-mask-image: -webkit-radial-gradient(white, black);
  clip-path: inset(0 round 16px);
`;

const HeroImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 0; /* rounding handled by wrapper */
  object-fit: contain;
  object-position: center;
  display: block;
`;

// Body copy
const Body = styled.div`
  font-family: var(--font-body);
  font-size: 1.0625rem;
  line-height: 1.75;
  margin-top: 1.5rem;
  color: #344054;

  p { margin: 0 0 1rem; }
  h2 {
    font-family: var(--font-display);
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    font-size: clamp(1.25rem, 2.5vw, 1.75rem);
    line-height: 1.2;
    margin: 2rem 0 0.75rem;
    color: #101828;
  }
  h3 {
    font-family: var(--font-display);
    font-weight: 600;
    letter-spacing: 0.015em;
    text-transform: uppercase;
    font-size: clamp(1.125rem, 2vw, 1.5rem);
    line-height: 1.25;
    margin: 1.25rem 0 0.5rem;
    color: #101828;
  }
  ul, ol { padding-left: 1.25rem; }
`;

// Substack embed wrapper
const EmbedWrap = styled.div`
  margin-top: 2.5rem;
  display: flex;
  justify-content: center;
`;

// New styled components for layout
const HeaderSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

// ===============
// Helpers
// ===============
function toHttps(url?: string): string | undefined {
  if (!url) return undefined;
  return url.startsWith('//') ? `https:${url}` : url;
}


function formatMMDDYYYY(input?: string): string | null {
  if (!input) return null;
  const d = new Date(input);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function getAssetId(input: unknown): string | null {
  if (!input) return null;
  if (typeof input === 'string' || typeof input === 'number') return String(input);
  if (isRecord(input)) {
    const sys = input['sys'];
    if (isRecord(sys)) {
      const id = sys['id'];
      if (typeof id === 'string' || typeof id === 'number') return String(id);
    }
  }
  return null;
}

function getAssetUrl(asset: unknown): string | undefined {
  if (!isRecord(asset)) return undefined;
  const fields = asset['fields'];
  if (!isRecord(fields)) return undefined;
  const file = fields['file'];
  if (!isRecord(file)) return undefined;
  const url = file['url'];
  if (typeof url === 'string') return toHttps(url);
  return undefined;
}

function getAuthorPictureIdFromPost(post: unknown): string | null {
  if (!isRecord(post)) return null;
  const pic = post['authorPicture'];
  return getAssetId(pic);
}

// ===============
// Main Component
// ===============
const BlogPostPage = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const blogPosts = useContext(BlogContext);
  const location = useLocation();
  const [currentBlog, setCurrentBlog] = useState<BlogPosts | null>(null);
  const [assets, setAssets] = useState<{ [id: string]: string }>({});

  // Scroll Progress Handler
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.body.scrollHeight;
      const progress = (scrollY / Math.max(1, documentHeight - windowHeight)) * 100;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch Assets (Images) for Blog Posts (hero + author avatar)
  useEffect(() => {
    blogPosts.forEach((post) => {
      // Main thumbnail / hero
      contentfulClient
        .getAsset(String(post.imageURL))
        .then((asset) => {
          const url = getAssetUrl(asset);
          if (url) setAssets((prev) => ({ ...prev, [String(post.id)]: url }));
        })
        .catch((error) => console.error('Error fetching asset:', error));

      // Author picture if available on the post
      const authorPicId = getAuthorPictureIdFromPost(post);
      if (authorPicId) {
        contentfulClient
          .getAsset(String(authorPicId))
          .then((asset) => {
            const url = getAssetUrl(asset);
            if (url) setAssets((prev) => ({ ...prev, [`author-${String(post.id)}`]: url }));
          })
          .catch((error) => console.error('Error fetching author asset:', error));
      }
    });
  }, [blogPosts]);

  // Determine the Current Blog Post Based on URL
  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    const foundBlog = blogPosts.find((post) => String(post.id) === lastSegment);
    setCurrentBlog(foundBlog || null);
  }, [location.pathname, blogPosts]);

  // Load brand fonts only for this page (no global CSS impact)
  useEffect(() => {
    const href = 'https://fonts.googleapis.com/css2?family=Alumni+Sans:wght@700;800&family=Bitter:wght@400;500;600;700&display=swap';
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  const formattedDate = formatMMDDYYYY(currentBlog?.date);
  const heroUrl = currentBlog ? assets[String(currentBlog.id)] : undefined;
  const avatarUrl = currentBlog ? assets[`author-${String(currentBlog.id)}`] : undefined;

  const [copied, setCopied] = useState(false);
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const encodedUrl = encodeURIComponent(currentUrl);
  const encodedTitle = encodeURIComponent(currentBlog?.title ?? '');
  const xShare = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
  const liShare = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
  const fbShare = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;

  const handleCopy = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(currentUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  };

  return (
    <PageWrap>
      {/* Scroll Progress Bar */}
      <ProgressBar style={{ width: `${scrollProgress}%` }} />

      {/* Article */}
      <Article>
        {currentBlog && (
          <>
            <HeaderSection>
              <TextSection>
                <Title>{currentBlog.title}</Title>
                <MetaBar>
                  {avatarUrl && <Avatar src={avatarUrl} alt={currentBlog.author || 'Author'} />}
                  {currentBlog.author && <span>{currentBlog.author}</span>}
                  {formattedDate && <span>• {formattedDate}</span>}
                  <Dot>•</Dot>
                  <ShareGroup>
                    {/* X/Twitter */}
                    <ShareLink href={xShare} target="_blank" rel="noopener noreferrer" aria-label="Share on X">
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M18.244 3H21l-6.54 7.47L22 21h-6.09l-4.77-5.7L5.6 21H3l6.99-7.98L2 3h6.19l4.31 5.22L18.244 3Zm-1.066 17h1.68L8.91 5H7.16l10.018 15Z" fill="currentColor"/>
                      </svg>
                    </ShareLink>
                    {/* LinkedIn */}
                    <ShareLink href={liShare} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn">
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M6.94 6.5A2.44 2.44 0 1 1 4.5 4.06 2.44 2.44 0 0 1 6.94 6.5ZM3.75 8.25h3.6V20.25h-3.6V8.25Zm6 0h3.45v1.64h.05a3.78 3.78 0 0 1 3.4-1.87c3.63 0 4.3 2.39 4.3 5.5v6.73h-3.6v-5.97c0-1.42-.03-3.24-1.98-3.24-1.98 0-2.29 1.55-2.29 3.13v6.08h-3.6V8.25Z" fill="currentColor"/>
                      </svg>
                    </ShareLink>
                    {/* Facebook */}
                    <ShareLink href={fbShare} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook">
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M13.5 9H16V6h-2.5C11.57 6 11 7.79 11 9.5V11H9v3h2v6h3v-6h2.1l.4-3H14v-1.25c0-.55.45-.75 1-.75Z" fill="currentColor"/>
                      </svg>
                    </ShareLink>
                    {/* Copy link */}
                    <ShareButton onClick={handleCopy} aria-label={copied ? 'Copied!' : 'Copy link'} title={copied ? 'Copied!' : 'Copy link'}>
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M16 1H8a2 2 0 0 0-2 2v10h2V3h8V1Zm3 4H10a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Zm0 14H10V7h9v12Z" fill="currentColor"/>
                      </svg>
                    </ShareButton>
                  </ShareGroup>
                </MetaBar>
              </TextSection>
              {heroUrl && (
                <Hero>
                  <HeroImg src={heroUrl} alt={currentBlog.title} />
                </Hero>
              )}
            </HeaderSection>

            {/* Blog content */}
            <Body>{currentBlog.body}</Body>

            {/* Substack embed */}
            <EmbedWrap>
              <iframe
                src="https://sbupodcastincubator.substack.com/embed"
                width="480"
                height="320"
                style={{ border: '1px solid #EEE', background: 'white' }}
                frameBorder={0}
                scrolling="no"
              />
            </EmbedWrap>
          </>
        )}
      </Article>
    </PageWrap>
  );
};

export default BlogPostPage;
